import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { AdminService } from 'src/app/shared';
import { ToastrService } from 'ngx-toastr';
import { LoaderComponent } from '../../shared/elements/loader/loader.component';
import { MultiselectGroupCheckboxComponent } from '../../shared/elements/multiselect-group-checkbox/multiselect-group-checkbox.component';
import { DropdownWithSearchComponent } from '../../shared/elements/dropdown-with-search/dropdown-with-search.component';

@Component({
    selector: 'app-user-accesss-add-popup',
    templateUrl: './user-accesss-add-popup.component.html',
    styleUrls: ['./user-accesss-add-popup.component.css'],
    imports: [LoaderComponent, MultiselectGroupCheckboxComponent, DropdownWithSearchComponent]
})
export class UserAccesssAddPopupComponent implements OnChanges {
  @Input() rowData: any = {};
  @Input() msId: string = '';
  @Input() mail: string = '';
  @Output() closed = new EventEmitter<void>();
  @Output() submitted = new EventEmitter<void>();

  showLoader: boolean = false;

  selectedFieldMap: { [label: string]: any } = {};

  popupFields: { label: string; type: string; name: any; default_selection?: string; depends_on?: { field: string; filter_by: string } }[] = [];
  popupQueryFilter: string = '';
  fieldDataMap: { [label: string]: any[] } = {};
  rawPopupData: any[] = [];
  /** Flat tree data keyed by field label – used for 'hierarchical' type fields */
  hierarchyDataMap: { [fieldLabel: string]: any[] } = {};
  /** Dynamic hierarchyDropdownConfig keyed by field label */
  hierarchyConfigMap: { [fieldLabel: string]: any } = {};
  /** Stores selectedHierarchy event output per hierarchical field */
  hierarchySelectionMap: { [fieldLabel: string]: any[] } = {};

  constructor(public adminService: AdminService, public toaster: ToastrService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['rowData']) {
      this.selectedFieldMap = {};
      this.popupFields = [];
      this.popupQueryFilter = '';
      this.fieldDataMap = {};
      this.rawPopupData = [];
      this.hierarchyDataMap = {};
      this.hierarchyConfigMap = {};
      this.hierarchySelectionMap = {};

      const rawPopupDetails = this.rowData?.Popup_Details;
      if (rawPopupDetails) {
        const parsed = typeof rawPopupDetails === 'string' ? JSON.parse(rawPopupDetails) : rawPopupDetails;
        this.popupQueryFilter = parsed.query_filter || '';
        this.popupFields = (parsed.fields || []).map((field: any) => ({
          label: field.label,
          type: field.type,
          name: field.name,
          default_selection: field.default_selection,
          depends_on: field.depends_on ?? null
        }));
      }
      if (this.popupFields.length > 0 && this.popupFields.every(f => f.default_selection === 'All')) {
        this.submitWithDefaults();
        return;
      }

      if (this.popupQueryFilter) {
        this.loadPopupData();
      }
    }
  }

  get displayRole(): string {
    const role = (this.rowData?.Role || '').toString();
    return role.replace(/ ?\((Yes|No|NA)\)/i, '').trim();
  }

  private submitWithDefaults(): void {
    const msId = this.msId;
    const currentUserMsId = localStorage.getItem('MsId') || '';
    const rowId = this.rowData?.ID || this.rowData?.id;
    const currentUser = (localStorage.getItem('currentUser') || '').replace(/"/g, '').trim();
    const entry: any = { ID: rowId, MSID: msId, Mail: this.mail, createdById: currentUserMsId, createdByName: currentUser };
    this.popupFields.forEach(field => {
      Object.values(field.name || {}).forEach((idKey: any) => {
        if (idKey) { entry[idKey] = 'All'; }
      });
    });
    this.adminService.addUserAccessDetails([entry]).subscribe(
      () => {
        this.toaster.success('Access added successfully', '', { timeOut: 5000, positionClass: 'toast-top-center' });
        this.submitted.emit();
        this.closed.emit();
      },
      () => {
        this.toaster.error('Something went wrong', '', { timeOut: 5000, positionClass: 'toast-top-center' });
        this.closed.emit();
      }
    );
  }

  loadPopupData(): void {
    const ID = this.rowData?.ID || this.rowData?.id;
    this.showLoader = true;
    this.adminService.getPopupDropdownData(ID).subscribe(
      (response: any) => {
        this.showLoader = false;
        const rows: any[] = Array.isArray(response) ? response : (response?.result || response?.data || []);
        this.rawPopupData = rows;
        const existingDetails: any[] = this.rowData?.childDataSource?.data || this.rowData?.details || [];
        this.popupFields.forEach(field => {
          // Dependent fields start empty — populated when parent selection changes
          if (field.depends_on) {
            this.fieldDataMap[field.label] = [];
            return;
          }
          if (field.type === 'hierarchical') {
            this.buildHierarchyFromField(field, rows, existingDetails);
            return;
          }
          this.buildFieldDropdown(field, rows, existingDetails);
        });
      },
      () => { this.showLoader = false; }
    );
  }

  /**
   * Common function that builds a nested hierarchy tree and its `hierarchyDropdownConfig`
   * from a field whose `label` is slash-separated level names and whose `name` map is
   * `{ levelLabel: idColumnName }`.
   *
   * Supports n levels (2 or 3 levels are rendered by app-multiselect-group-checkbox).
   * Results are stored in hierarchyDataMap and hierarchyConfigMap keyed by field.label.
   */
  buildHierarchyFromField(field: any, rows: any[], existingDetails: any[] = []): void {
    const levelLabels: string[] = (field.label || '')
      .split('/')
      .map((s: string) => s.trim())
      .filter(Boolean);
    const nameMap: { [key: string]: string } = field.name || {};
    const n = levelLabels.length;
    if (n < 1) { return; }

    const buildNodes = (levelIdx: number, filteredRows: any[]): any[] => {
      const lvlLabel = levelLabels[levelIdx];
      const idCol = nameMap[lvlLabel];
      const isLeaf = levelIdx === n - 1;
      // The child-array key on a parent node is the NEXT level's display label.
      // This dual-purpose key also serves as the display property on child nodes,
      // which is what app-multiselect-group-checkbox reads for label text.
      const childKey = !isLeaf ? levelLabels[levelIdx + 1] : null;

      const existingIds = isLeaf
        ? new Set<string>(
            existingDetails
              .map((r: any) => { const v = r[idCol]; return v != null ? String(v) : null; })
              .filter(Boolean) as string[]
          )
        : null;

      const seen = new Set<string>();
      const nodes: any[] = [];

      filteredRows.forEach((row: any) => {
        const id = row[idCol];
        if (id == null || seen.has(String(id))) { return; }
        seen.add(String(id));

        const node: any = {
          [idCol]: id,
          [lvlLabel]: row[lvlLabel] ?? id,
          disable: isLeaf ? existingIds!.has(String(id)) : false
        };

        if (!isLeaf && childKey) {
          const childRows = filteredRows.filter((r: any) => String(r[idCol] ?? '') === String(id));
          const children = buildNodes(levelIdx + 1, childRows);
          node[childKey] = children;
          node.disable = children.length > 0 && children.every((c: any) => c.disable);
        }

        nodes.push(node);
      });

      return nodes;
    };

    this.hierarchyDataMap[field.label] = buildNodes(0, rows);

    // Build the hierarchyDropdownConfig compatible with app-multiselect-group-checkbox.
    // For 2 levels:  secondchildfield=[], secondchildkey=''
    // For 3+ levels: secondchildfield and secondchildkey use the 3rd level label
    const config: any = {
      parentfield:      [nameMap[levelLabels[0]], levelLabels[0]],
      firstchildfield:  n >= 2 ? [nameMap[levelLabels[1]], levelLabels[1]] : [],
      secondchildfield: n >= 3 ? [nameMap[levelLabels[2]], levelLabels[2], levelLabels[2]] : [],
      firstchildkey:    n >= 2 ? levelLabels[1] : '',
      secondchildkey:   n >= 3 ? levelLabels[2] : ''
    };

    this.hierarchyConfigMap[field.label] = config;
  }

  private buildFieldDropdown(field: any, rows: any[], existingDetails: any[]): void {
    const nameMap = field.name || {};
    const displayColKey = (Object.keys(nameMap)[0] || '');
    const idColKey = Object.values(nameMap)[0] as string;
    if (!displayColKey || !idColKey) { return; }
    const existingIds = new Set(
      existingDetails.map(r => {
        const v = r[idColKey] ?? r[displayColKey];
        return v != null ? String(v) : null;
      }).filter(Boolean)
    );

    // Find child fields that depend on this field
    const childFields = this.popupFields.filter(f => f.depends_on?.field === field.label);

    const seen = new Set<string>();
    this.fieldDataMap[field.label] = [];
    rows.forEach(row => {
      const id = row[idColKey] ?? row[displayColKey];
      const name = row[displayColKey] ?? row[displayColKey.replace(/ /g, '_')] ?? id;
      if (id != null && !seen.has(String(id))) {
        seen.add(String(id));

        let disabled: boolean;
        if (childFields.length > 0) {
          // For a parent field: disable only if ALL dependent child items are already in existingDetails
          disabled = childFields.every(childField => {
            const childNameMap = childField.name || {};
            const childDisplayColKey = Object.keys(childNameMap)[0] || '';
            const childIdColKey = Object.values(childNameMap)[0] as string;
            const filterCol = childField.depends_on!.filter_by;
            const childRows = this.rawPopupData.filter(r => String(r[filterCol] ?? '') === String(id));
            if (childRows.length === 0) { return false; }
            const existingChildIds = new Set(
              existingDetails.map(r => {
                const v = r[childIdColKey] ?? r[childDisplayColKey];
                return v != null ? String(v) : null;
              }).filter(Boolean)
            );
            return childRows.every(childRow => {
              const childId = childRow[childIdColKey] ?? childRow[childDisplayColKey];
              return childId != null && existingChildIds.has(String(childId));
            });
          });
        } else {
          disabled = existingIds.has(String(id));
        }

        // _raw preserves the original row so onSubmit can map multiple name-config entries exactly
        this.fieldDataMap[field.label].push({ name, id, disabled, _raw: row });
      }
    });
  }

  private refreshDependentDropdowns(parentField: any, parentId: string | null): void {
    const existingDetails: any[] = this.rowData?.childDataSource?.data || this.rowData?.details || [];
    this.popupFields
      .filter(f => f.depends_on?.field === parentField.label)
      .forEach(childField => {
        this.selectedFieldMap[childField.label] = null;
        const filterCol = childField.depends_on!.filter_by;
        const filteredRows = parentId
          ? this.rawPopupData.filter(r => String(r[filterCol] ?? '') === String(parentId))
          : [];
        this.buildFieldDropdown(childField, filteredRows, existingDetails);
      });
  }

  getFieldDropdownData(field: any): any[] {
    return this.fieldDataMap[field.label] || [];
  }

  getHierarchyData(field: any): any[] {
    return this.hierarchyDataMap[field.label] || [];
  }

  getHierarchyConfig(field: any): any {
    return this.hierarchyConfigMap[field.label] || {};
  }

  onHierarchySelectionChange(field: any, event: any): void {
    this.hierarchySelectionMap[field.label] = Array.isArray(event) ? event : [];
  }

  getFieldSelected(field: any): any {
    return this.selectedFieldMap[field.label] ?? null;
  }

  isFieldDisabled(field: any): boolean {
    if (field.depends_on) {
      const parentField = this.popupFields.find(f => f.label === field.depends_on!.field);
      if (parentField) {
        const parentSel = this.getFieldSelected(parentField);
        return parentSel == null || (Array.isArray(parentSel) && parentSel.length === 0);
      }
    }
    return false;
  }

  get isSubmitDisabled(): boolean {
    return this.popupFields.some(field => {
      if (field.type === 'hierarchical') {
        return !(this.hierarchySelectionMap[field.label]?.length > 0);
      }
      if (field.type === 'singleselect') {
        const sel = this.getFieldSelected(field);
        if (sel == null) { return true; }
        if (Array.isArray(sel)) { return sel.length === 0; }
        return false;
      }
      if (field.type === 'multiselect') {
        if (this.isFieldDisabled(field)) { return false; }
        const sel = this.getFieldSelected(field);
        return !Array.isArray(sel) || sel.length === 0;
      }
      return false;
    });
  }

  onFieldSelectionChange(field: any, event: any): void {
    if (field.type === 'multiselect' && event?.data && Array.isArray(event.data)) {
      event = { ...event, data: event.data.filter((item: any) => !item.disabled) };
    }
    const rawSelected = event?.data ?? event;
    this.selectedFieldMap[field.label] = Array.isArray(rawSelected)
      ? rawSelected.filter((s: any) => !s.disabled)
      : rawSelected;
    if (this.rawPopupData.length > 0) {
      const sel = this.getFieldSelected(field);
      const parentId = sel?.id ?? sel?.name ?? null;
      this.refreshDependentDropdowns(field, parentId);
    }
  }

  onSubmit(): void {
    const msId = this.msId;
    const rowId = this.rowData?.ID || this.rowData?.id;
    const payload: any[] = [];

    const hierarchicalField = this.popupFields.find(f => f.type === 'hierarchical');

    if (hierarchicalField) {
        const levelLabels: string[] = (hierarchicalField.label || '')
          .split('/')
          .map((s: string) => s.trim())
          .filter(Boolean);
        const nameMap: { [k: string]: string } = hierarchicalField.name || {};
        const selections: any[] = this.hierarchySelectionMap[hierarchicalField.label] || [];
        selections.forEach((billingObj: any) => {
          this.buildHierarchicalPayloadEntries(billingObj, levelLabels, nameMap, msId, rowId, payload);
        });
    } else {
        const currentUser = (localStorage.getItem('currentUser') || '').replace(/"/g, '').trim();
        const currentUserMsId = localStorage.getItem('MsId') || '';
        const baseEntry: any = { ID: rowId, MSID: msId, Mail: this.mail, createdById: currentUserMsId, createdByName: currentUser };

        // Dynamic: iterate every { displayKey: dbColumn } entry in the name config
        this.popupFields.filter(f => f.type === 'singleselect').forEach(field => {
          const sel = this.getFieldSelected(field);
          Object.entries(field.name || {}).forEach(([displayKey, dbCol]) => {
            const value = sel?._raw?.[dbCol as string] ?? sel?._raw?.[displayKey] ?? sel?.id ?? sel?.name ?? 'All';
            baseEntry[dbCol as string] = value;
          });
        });

        const multiSelectField = this.popupFields.find(f => f.type === 'multiselect');
        if (multiSelectField) {
          const selected: any[] = (Array.isArray(this.getFieldSelected(multiSelectField))
            ? this.getFieldSelected(multiSelectField)
            : []).filter((s: any) => !s.disabled);
          if (selected.length) {
            // Dynamic: each name-config entry { displayKey: dbColumn } maps to the item's _raw row value
            selected.forEach((item: any) => {
              const entry: any = { ...baseEntry };
              Object.entries(multiSelectField.name || {}).forEach(([displayKey, dbCol]) => {
                const value = item._raw?.[dbCol as string] ?? item._raw?.[displayKey] ?? item.id ?? item.name ?? item;
                entry[dbCol as string] = value;
              });
              payload.push(entry);
            });
          } else {
            const entry: any = { ...baseEntry };
            Object.entries(multiSelectField.name || {}).forEach(([, dbCol]) => {
              entry[dbCol as string] = 'All';
            });
            payload.push(entry);
          }
        } else {
          payload.push(baseEntry);
        }
      }

    this.adminService.addUserAccessDetails(payload).subscribe(
      () => {
        this.toaster.success('Access added successfully', '', { timeOut: 5000, positionClass: 'toast-top-center' });
        this.submitted.emit();
        this.closed.emit();
      },
      () => {
        this.toaster.error('Something went wrong', '', { timeOut: 5000, positionClass: 'toast-top-center' });
        this.closed.emit();
      }
    );
  }

  private buildHierarchicalPayloadEntries(
    billingObj: any,
    levelLabels: string[],
    nameMap: { [k: string]: string },
    msId: string,
    rowId: any,
    payload: any[]
  ): void {
    const currentUserMsId = localStorage.getItem('MsId') || '';
    const currentUser = (localStorage.getItem('currentUser') || '').replace(/"/g, '').trim();
    const base = { MSID: msId, ID: rowId, Mail: this.mail, createdById: currentUserMsId, createdByName: currentUser };
    const n = levelLabels.length;

    if (n >= 3) {
      // 3-level: billingObj carries parentfield[0] (level0 ID) and firstchildfield[0] (level1 ID)
      // directly, plus secondchildkey array of leaf IDs.
      const leafKey = levelLabels[2];
      const leafIds: any[] = billingObj[leafKey] || [];
      leafIds.forEach((leafId: any) => {
        payload.push({
          ...base,
          [nameMap[levelLabels[0]]]: billingObj[nameMap[levelLabels[0]]],
          [nameMap[levelLabels[1]]]: billingObj[nameMap[levelLabels[1]]],
          [nameMap[levelLabels[2]]]: leafId
        });
      });
    } else if (n === 2) {
      // 2-level: billingObj carries parent display name; look up parent ID from rawPopupData.
      const parentDisplay = billingObj[levelLabels[0]];
      const parentRow = this.rawPopupData.find((r: any) => r[levelLabels[0]] === parentDisplay);
      const parentId = parentRow?.[nameMap[levelLabels[0]]];
      const childIds: any[] = billingObj[levelLabels[1]] || [];
      childIds.forEach((childId: any) => {
        payload.push({ ...base, [nameMap[levelLabels[0]]]: parentId, [nameMap[levelLabels[1]]]: childId });
      });
    }
  }

  onClose(): void {
    this.closed.emit();
  }
}
